import { useState, useCallback, useRef, useEffect } from "react";
import { useCmv } from "@/hooks/useCmv";
import { LoadingScreen } from "@/components/layout/LoadingScreen";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { FileText, Download, Receipt, TrendingUp, TrendingDown, Wallet, Loader2, ChevronRight, Trash2 } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function DrillDownModal({ open, onClose, month, category, categoryLabel, formatPrice }) {
  const { authAxios } = useAuth();
  const authAxiosRef = useRef(authAxios);
  authAxiosRef.current = authAxios;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [deletingId, setDeletingId] = useState(null);

  const fetchDrillDown = useCallback(async () => {
    if (!category || !month) return;
    setLoading(true);
    setData(null);
    try {
      const res = await authAxiosRef.current.get(
        `/financiero/cmv/drill-down?month=${month}&category=${encodeURIComponent(category)}`
      );
      setData(res.data);
    } catch {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [category, month]);

  useEffect(() => {
    if (open && category) fetchDrillDown();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, category, month]);

  const deleteBill = useCallback(async (billId, allocated) => {
    const label = allocated != null ? ` (${allocated}€)` : "";
    if (!window.confirm(`Eliminar esta fatura${label}? Esta ação não pode ser desfeita.`)) return;
    setDeletingId(billId);
    try {
      await authAxiosRef.current.delete(`/financiero/bills/${billId}`);
      await fetchDrillDown();
    } catch {
      alert("Erro ao eliminar a fatura. Tenta novamente.");
    } finally {
      setDeletingId(null);
    }
  }, [fetchDrillDown]);

  const formatDate = (d) => {
    if (!d) return "—";
    try { return new Date(d + "T00:00:00").toLocaleDateString("pt-PT"); } catch { return d; }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="text-base font-bold">
            Detalhamento — {categoryLabel || category}
          </DialogTitle>
        </DialogHeader>

        <div className="flex-1 overflow-auto">
          {loading && (
            <div className="flex items-center justify-center py-12 gap-2 text-muted-foreground">
              <Loader2 className="w-5 h-5 animate-spin" />
              <span className="text-sm">A carregar...</span>
            </div>
          )}

          {!loading && data && data.bills.length === 0 && (
            <p className="text-center py-12 text-sm text-muted-foreground">Nenhuma fatura encontrada nesta categoria.</p>
          )}

          {!loading && data && data.bills.length > 0 && (
            <div className="text-sm">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 text-xs text-muted-foreground uppercase">
                    <th className="text-left py-2 pr-3 font-semibold">Data</th>
                    <th className="text-left py-2 pr-3 font-semibold">Fornecedor</th>
                    <th className="text-left py-2 pr-3 font-semibold hidden sm:table-cell">Descrição</th>
                    <th className="text-left py-2 pr-3 font-semibold hidden md:table-cell">Documento</th>
                    <th className="text-center py-2 pr-3 font-semibold">Status</th>
                    <th className="text-right py-2 pr-3 font-semibold">Valor</th>
                    <th className="py-2 w-8" />
                  </tr>
                </thead>
                <tbody>
                  {data.bills.map((bill, i) => (
                    <tr key={i} className={`border-b border-gray-100 hover:bg-gray-50 transition-colors${bill.allocated > 1000 ? " bg-red-50/40" : ""}` }>
                      <td className="py-2 pr-3 text-gray-600 whitespace-nowrap">{formatDate(bill.date)}</td>
                      <td className="py-2 pr-3 font-medium max-w-[120px] truncate">{bill.supplier || "—"}</td>
                      <td className="py-2 pr-3 text-gray-500 max-w-[160px] truncate hidden sm:table-cell">{bill.description || "—"}</td>
                      <td className="py-2 pr-3 text-gray-400 text-xs hidden md:table-cell">{bill.doc_number || "—"}</td>
                      <td className="py-2 pr-3 text-center">
                        <Badge className="text-xs bg-emerald-100 text-emerald-700 px-1.5 py-0">Pago</Badge>
                      </td>
                      <td className="py-2 pr-3 text-right font-bold whitespace-nowrap">
                        {formatPrice(bill.allocated)}
                      </td>
                      <td className="py-2 text-center">
                        {bill.id && (
                          <button
                            onClick={() => deleteBill(bill.id, bill.allocated?.toFixed(2))}
                            disabled={deletingId === bill.id}
                            className="text-gray-300 hover:text-red-500 transition-colors disabled:opacity-40"
                            title="Eliminar fatura"
                          >
                            {deletingId === bill.id
                              ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                              : <Trash2 className="w-3.5 h-3.5" />}
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-300 bg-gray-50">
                    <td colSpan={5} className="py-3 pr-3 font-black text-sm uppercase">TOTAL</td>
                    <td className="py-3 pr-3 text-right font-black text-base">{formatPrice(data.total)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {!loading && !data && (
            <p className="text-center py-12 text-sm text-muted-foreground">Erro ao carregar detalhes.</p>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export const CmvTab = () => {
  const { report, cashPosition, loading, month, setMonth, formatPrice, barColor, exportPDF, exportExcelCMV, saveSnapshot } = useCmv();
  const [drill, setDrill] = useState(null);
  const [snapshotDialog, setSnapshotDialog] = useState(null); // null | {opening: string, closing: string}
  const [savingSnapshot, setSavingSnapshot] = useState(false);

  if (loading) return <LoadingScreen />;
  if (!report) return <p className="text-center py-8 text-muted-foreground">Erro ao carregar</p>;

  const openDrill = (item) => setDrill({ category: item.category, categoryLabel: item.label });
  const closeDrill = () => setDrill(null);

  const openSnapshotDialog = () => {
    if (!cashPosition) return;
    setSnapshotDialog({
      opening: String(cashPosition.saldo_inicial_mes ?? 0),
      closing: String(cashPosition.caixa_disponivel_real ?? 0),
    });
  };

  const handleSnapshotOpeningChange = (val) => {
    const op = parseFloat(val) || 0;
    const rev = cashPosition?.total_recebido_mes ?? 0;
    const paid = cashPosition?.total_pago_mes ?? 0;
    const pend = cashPosition?.contas_a_pagar_pendentes ?? 0;
    setSnapshotDialog(d => ({ ...d, opening: val, closing: String(+(op + rev - paid - pend).toFixed(2)) }));
  };

  const handleSaveSnapshot = async () => {
    if (!snapshotDialog) return;
    setSavingSnapshot(true);
    const origOpening = cashPosition?.saldo_inicial_mes ?? 0;
    const editedOpening = parseFloat(snapshotDialog.opening) || 0;
    await saveSnapshot(
      month,
      Math.abs(editedOpening - origOpening) > 0.005 ? snapshotDialog.opening : undefined,
      snapshotDialog.closing,
    );
    setSnapshotDialog(null);
    setSavingSnapshot(false);
  };

  const MONTH_NAMES = ["","janeiro","fevereiro","março","abril","maio","junho","julho","agosto","setembro","outubro","novembro","dezembro"];
  const humanMonth = (ym) => {
    if (!ym) return ym;
    const [y, m] = ym.split("-");
    return `${MONTH_NAMES[parseInt(m)] || m} de ${y}`;
  };

  return (
    <div className="space-y-4" data-testid="cmv-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <p className="text-sm text-muted-foreground">Demonstrativo de Resultados (DRE) e CMV</p>
        <div className="flex gap-2 items-center">
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportPDF} data-testid="export-pdf-btn"><FileText className="w-3 h-3" />PDF</Button>
          <Button variant="outline" size="sm" className="gap-1 text-xs" onClick={exportExcelCMV} data-testid="export-excel-cmv-btn"><Download className="w-3 h-3" />Excel</Button>
          <Input type="month" value={month} onChange={e => setMonth(e.target.value)} className="w-44" data-testid="cmv-month" />
        </div>
      </div>

      <Card className="bg-emerald-50/50 border-emerald-200">
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-emerald-700 font-bold uppercase">Faturamento</p>
              <p className="text-3xl font-black text-emerald-800">{formatPrice(report.revenue)}</p>
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">{report.orders_count} pedidos</p>
              <p className="text-sm font-bold text-emerald-700">100%</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {report.materia_prima.length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase mb-3">CMV - Matéria Prima</h3>
            <div className="space-y-1">
              {report.materia_prima.map(item => (
                <button
                  key={item.category}
                  onClick={() => openDrill(item)}
                  className="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-emerald-50 transition-colors group text-left"
                  data-testid={`drill-${item.category}`}
                >
                  <span className="text-sm flex-1 truncate group-hover:text-emerald-700">{item.label}</span>
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, item.pct * 2)}%`, backgroundColor: barColor(item.pct) }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-10 text-right">{item.pct}%</span>
                  <span className="text-sm font-bold w-20 text-right">{formatPrice(item.total)}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-emerald-600 shrink-0" />
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-sm font-bold">Total Matéria Prima</span>
              <div className="flex items-center gap-3">
                <Badge className={`${report.pct_materia_prima > 35 ? "bg-red-100 text-red-700" : report.pct_materia_prima > 25 ? "bg-amber-100 text-amber-700" : "bg-emerald-100 text-emerald-700"}`}>{report.pct_materia_prima}%</Badge>
                <span className="font-bold">{formatPrice(report.total_materia_prima)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {report.custos_fixos.length > 0 && (
        <Card className="border-border/50">
          <CardContent className="p-4">
            <h3 className="text-xs font-bold text-muted-foreground uppercase mb-3">Custos Fixos e Operacionais</h3>
            <div className="space-y-1">
              {report.custos_fixos.map(item => (
                <button
                  key={item.category}
                  onClick={() => openDrill(item)}
                  className="w-full flex items-center gap-3 px-2 py-1.5 rounded-lg hover:bg-indigo-50 transition-colors group text-left"
                  data-testid={`drill-${item.category}`}
                >
                  <span className="text-sm flex-1 truncate group-hover:text-indigo-700">{item.label}</span>
                  <div className="w-24 h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${Math.min(100, item.pct * 2)}%`, backgroundColor: "#6366f1" }} />
                  </div>
                  <span className="text-xs text-muted-foreground w-10 text-right">{item.pct}%</span>
                  <span className="text-sm font-bold w-20 text-right">{formatPrice(item.total)}</span>
                  <ChevronRight className="w-3.5 h-3.5 text-muted-foreground group-hover:text-indigo-600 shrink-0" />
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between mt-3 pt-3 border-t">
              <span className="text-sm font-bold">Total Custos Fixos</span>
              <div className="flex items-center gap-3">
                <Badge className="bg-indigo-100 text-indigo-700">{report.pct_custos_fixos}%</Badge>
                <span className="font-bold">{formatPrice(report.total_custos_fixos)}</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      <Card className={`${report.result_liquid >= 0 ? "bg-emerald-50/50 border-emerald-200" : "bg-red-50/50 border-red-200"}`}>
        <CardContent className="p-5">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-bold uppercase" style={{ color: report.result_liquid >= 0 ? "#059669" : "#dc2626" }}>Resultado Líquido</p>
              <p className="text-3xl font-black" style={{ color: report.result_liquid >= 0 ? "#047857" : "#dc2626" }}>{formatPrice(report.result_liquid)}</p>
            </div>
            <div className="text-right">
              <Badge className={`text-lg px-3 py-1 ${report.result_liquid >= 0 ? "bg-emerald-100 text-emerald-800" : "bg-red-100 text-red-800"}`}>{report.pct_result}%</Badge>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-current/10 grid grid-cols-1 sm:grid-cols-3 gap-3 text-xs">
            <div><p className="text-gray-500">Faturamento</p><p className="font-bold">{formatPrice(report.revenue)}</p></div>
            <div><p className="text-gray-500">Custos (base s/ IVA)</p><p className="font-bold text-red-600">-{formatPrice(report.total_costs)}</p></div>
            <div><p className="text-gray-500">Margem</p><p className="font-bold">{report.pct_result}%</p></div>
          </div>
          {report.total_iva > 0 && (
            <>
              <div className="flex justify-between mt-2 pt-2 border-t border-current/10 text-xs">
                <p className="text-gray-500">IVA Suportado (não entra no CMV)</p>
                <p className="font-bold text-amber-700">-{formatPrice(report.total_iva)}</p>
              </div>
              <div className="flex justify-between mt-1 text-xs">
                <p className="font-bold text-gray-700">Total gasto real (com IVA)</p>
                <p className="font-bold text-red-700">-{formatPrice(report.total_costs + report.total_iva)}</p>
              </div>
            </>
          )}
        </CardContent>
      </Card>

      {cashPosition && (
        <Card className={`border-2 ${cashPosition.caixa_disponivel_real >= 0 ? "border-blue-300 bg-blue-50/40" : "border-red-300 bg-red-50/40"}`}>
          <CardContent className="p-5">
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <Wallet className="w-5 h-5 text-blue-600" />
                <h3 className="text-sm font-bold uppercase text-blue-700">Posição de Caixa Real</h3>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="text-xs h-7 px-2 border-blue-300 text-blue-700 hover:bg-blue-50"
                onClick={openSnapshotDialog}
                title="Confirmar e guardar o fecho deste mês"
              >
                Fechar mês
              </Button>
            </div>
            <p className="text-[11px] text-blue-500 mb-4">Baseado em movimentação financeira real (c/ IVA)</p>
            <div className={`rounded-xl p-4 mb-4 text-center ${cashPosition.caixa_disponivel_real >= 0 ? "bg-blue-600" : "bg-red-600"}`}>
              <p className="text-xs font-semibold text-white/80 uppercase mb-1">Caixa disponível hoje</p>
              <p className="text-4xl font-black text-white">{formatPrice(cashPosition.caixa_disponivel_real)}</p>
            </div>
            <div className="space-y-2 text-sm">
              <div className="flex items-start justify-between py-2 border-b border-blue-100">
                <div className="flex flex-col gap-0.5">
                  <div className="flex items-center gap-2 text-gray-600"><span className="w-2 h-2 rounded-full bg-gray-400 inline-block" />Saldo inicial do mês</div>
                  {(cashPosition.saldo_inicial_fonte === "snapshot" || cashPosition.saldo_inicial_fonte === "fechamento_mes_anterior") && cashPosition.mes_referencia && (
                    <span className="text-[11px] text-emerald-600 pl-4">
                      {cashPosition.saldo_inicial_fonte === "snapshot" ? "Fecho confirmado de" : "Vem do fechamento de"} {humanMonth(cashPosition.mes_referencia)}
                    </span>
                  )}
                  {cashPosition.saldo_inicial_fonte === "calculado" && cashPosition.mes_referencia && (
                    <span className="text-[11px] text-blue-600 pl-4">
                      Calculado automaticamente de {humanMonth(cashPosition.mes_referencia)}
                    </span>
                  )}
                  {cashPosition.saldo_inicial_fonte === "sem_historico" && (
                    <span className="text-[11px] text-amber-600 pl-4">Primeiro mês operacional — saldo inicial zero</span>
                  )}
                </div>
                <span className="font-semibold">{formatPrice(cashPosition.saldo_inicial_mes)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-blue-100">
                <div className="flex items-center gap-2 text-emerald-700"><TrendingUp className="w-4 h-4" />Total recebido</div>
                <span className="font-bold text-emerald-700">+{formatPrice(cashPosition.total_recebido_mes)}</span>
              </div>
              <div className="flex items-center justify-between py-2 border-b border-blue-100">
                <div className="flex items-center gap-2 text-red-600"><TrendingDown className="w-4 h-4" />Faturas pagas (c/ IVA real)</div>
                <span className="font-bold text-red-600">-{formatPrice(cashPosition.total_pago_mes)}</span>
              </div>
              {cashPosition.contas_a_pagar_pendentes > 0 && (
                <div className="flex items-center justify-between py-2 border-b border-blue-100">
                  <div className="flex items-center gap-2 text-amber-700"><TrendingDown className="w-4 h-4" />Faturas pendentes (c/ IVA)</div>
                  <span className="font-bold text-amber-700">-{formatPrice(cashPosition.contas_a_pagar_pendentes)}</span>
                </div>
              )}
              <div className={`flex items-center justify-between pt-3 mt-1 border-t-2 ${cashPosition.caixa_disponivel_real >= 0 ? "border-blue-400" : "border-red-400"}`}>
                <span className="font-bold text-gray-800">=  Caixa disponível real</span>
                <span className={`text-lg font-black ${cashPosition.caixa_disponivel_real >= 0 ? "text-blue-700" : "text-red-700"}`}>
                  {formatPrice(cashPosition.caixa_disponivel_real)}
                </span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {report.materia_prima.length === 0 && report.custos_fixos.length === 0 && (
        <Card className="border-border/50">
          <CardContent className="py-12 text-center">
            <Receipt className="w-10 h-10 mx-auto text-muted-foreground mb-3" />
            <p className="text-muted-foreground font-medium">Sem faturas lancadas neste mes</p>
            <p className="text-xs text-muted-foreground mt-1">Lance faturas na aba "Faturas" para ver o CMV</p>
          </CardContent>
        </Card>
      )}

      <DrillDownModal
        open={!!drill}
        onClose={closeDrill}
        month={month}
        category={drill?.category}
        categoryLabel={drill?.categoryLabel}
        formatPrice={formatPrice}
      />

      <Dialog open={!!snapshotDialog} onOpenChange={(v) => { if (!v) setSnapshotDialog(null); }}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-base font-bold">Fechar mês — {humanMonth(month)}</DialogTitle>
          </DialogHeader>
          {snapshotDialog && (
            <div className="space-y-4 py-2">
              <p className="text-xs text-muted-foreground">
                Edita os valores se necessário. O saldo final guardado aqui torna-se o saldo inicial do próximo mês.
              </p>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Saldo inicial do mês</label>
                <Input
                  type="number"
                  step="0.01"
                  value={snapshotDialog.opening}
                  onChange={e => handleSnapshotOpeningChange(e.target.value)}
                  className="text-sm"
                />
                <p className="text-[11px] text-muted-foreground">Altera também o saldo final calculado automaticamente.</p>
              </div>
              <div className="space-y-1">
                <label className="text-xs font-semibold text-gray-600">Saldo final do mês (fecho real)</label>
                <Input
                  type="number"
                  step="0.01"
                  value={snapshotDialog.closing}
                  onChange={e => setSnapshotDialog(d => ({ ...d, closing: e.target.value }))}
                  className="text-sm font-bold"
                />
                <p className="text-[11px] text-muted-foreground">Este valor será o saldo inicial de {(() => { const [y,m]=month.split("-").map(Number); return humanMonth(m===12?`${y+1}-01`:`${y}-${String(m+1).padStart(2,'0')}`); })()}.</p>
              </div>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button variant="ghost" size="sm" onClick={() => setSnapshotDialog(null)}>Cancelar</Button>
            <Button
              size="sm"
              onClick={handleSaveSnapshot}
              disabled={savingSnapshot}
              className="bg-blue-600 hover:bg-blue-700 text-white"
            >
              {savingSnapshot ? <Loader2 className="w-3 h-3 animate-spin" /> : "Confirmar fecho"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
